import { useEffect, useState } from 'react';

import { SquareLock02Icon } from '@hugeicons/core-free-icons';
import { Permissions } from 'constants/permissions';
import { useAccessControl } from 'hooks/access-control';
import AppPage from 'layouts/AppPage/AppPage';
import { Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { IconComponent } from 'components/Icon/IconComponent';
import { OverflowTabs } from 'components/Tabs/Tabs';

import { messagePopUp } from 'api/app-slice';
import { getBrands, selectBrands } from 'api/brands-slice';
import { useLazyGetCheckRenewalsQuery } from 'api/check-renewals-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { useGetRenewalConfigsQuery } from 'api/renewals-config-api';
import { useGetCompanyUsersQuery } from 'api/user-api';
import { selectPermissions, selectPermissionsLoaded } from 'api/user-permissions-slice';
import { selectUser } from 'api/user-slice';

import styles from './Renewals.module.scss';
import { CheckRenewalsInProgressTab } from './tabs/CheckRenewalsInProgressTab/CheckRenewalsInProgressTab';
import { CheckRenewalsPendingTab } from './tabs/CheckRenewalsPendingTab/CheckRenewalsPendingTab';
import { RenewalResultsTab } from './tabs/RenewalResultsTab/RenewalResultsTab';
import { RenewalsConfigurationTab } from './tabs/RenewalsConfigurationTab/RenewalsConfigurationTab';
import { RenewalsEmployeesTab } from './tabs/RenewalsEmployeesTab/RenewalsEmployeesTab';

const Renewals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { tab: targetTab } = useParams();
  const { t } = useTranslation();

  const companyChecks = useSelector(selectCompanyChecks);
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const brands = useSelector(selectBrands);
  const permissions = useSelector(selectPermissions);
  const permissionsLoaded = useSelector(selectPermissionsLoaded);
  const hasAccess = useAccessControl(permissions);
  const hasRenewalsAccess = hasAccess(Permissions.RenewalsWrite);
  const [tab, setTab] = useState(targetTab);

  const [getRenewals] = useLazyGetCheckRenewalsQuery();

  const { data: renewalConfigsData } = useGetRenewalConfigsQuery(
    {},
    { skip: !permissionsLoaded || !hasRenewalsAccess },
  );
  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    skip: !permissionsLoaded || !hasRenewalsAccess,
  });

  const [renewalConfigs, setRenewalConfigs] = useState();
  const [loading, setLoading] = useState(false);

  const handleMessagePopUp = (text, state, hide = true) =>
    dispatch(messagePopUp({ text, state, hide }));

  useEffect(() => {
    if (permissionsLoaded && hasRenewalsAccess) dispatch(getBrands());
  }, [permissionsLoaded, hasRenewalsAccess]);

  useEffect(() => {
    setRenewalConfigs(renewalConfigsData);
  }, [renewalConfigsData]);

  useEffect(() => {
    if (renewalConfigs?.length === 0) {
      setTab('configuration');
    } else {
      setTab(targetTab);
    }
  }, [targetTab, renewalConfigs]);

  const setTabPath = key => {
    setTab(key);
    navigate(`/renewals/${key}`);
  };

  return (
    <AppPage loading={loading || !permissionsLoaded}>
      <div className={styles.root}>
        <AppPage.Header title={t('renewals.title')} subtitle={t('renewals.subtitle')} />
        {permissionsLoaded && !hasRenewalsAccess && (
          <div
            className="d-flex flex-column align-items-center justify-content-center u-gap"
            style={{ minHeight: '400px', padding: '80px 20px' }}>
            <IconComponent icon={SquareLock02Icon} size="xlarge" type="secondary" />
            <h4 className="u-font-weight-500">{t('renewals.noAccessTitle')}</h4>
            <p className="secondary-text text-center" style={{ maxWidth: '420px' }}>
              {t('renewals.noAccess')}
            </p>
          </div>
        )}
        {permissionsLoaded && hasRenewalsAccess && (
          <OverflowTabs
            defaultActiveKey="upcoming"
            activeKey={tab}
            onSelect={setTabPath}
            mountOnEnter
            className="mt-12 mx-12">
            <Tab
              eventKey="upcoming"
              title={t('renewals.tabs.upcoming')}
              disabled={renewalConfigs?.length === 0}>
              <CheckRenewalsPendingTab
                renewalConfigs={renewalConfigs}
                company={company}
                messagePopUp={handleMessagePopUp}
                setLoading={setLoading}
                getRenewals={getRenewals}
              />
            </Tab>
            <Tab
              eventKey="in-progress"
              title={t('renewals.tabs.inProgress')}
              disabled={renewalConfigs?.length === 0}>
              <CheckRenewalsInProgressTab
                renewalConfigs={renewalConfigs}
                company={company}
                messagePopUp={handleMessagePopUp}
                setLoading={setLoading}
                getRenewals={getRenewals}
              />
            </Tab>
            <Tab
              eventKey="results"
              title={t('renewals.tabs.results')}
              disabled={renewalConfigs?.length === 0}>
              <RenewalResultsTab company={company} setLoading={setLoading} />
            </Tab>
            <Tab
              eventKey="employees"
              title={t('renewals.tabs.employees')}
              disabled={renewalConfigs?.length === 0}>
              <RenewalsEmployeesTab
                company={company}
                setLoading={setLoading}
                messagePopUp={handleMessagePopUp}
              />
            </Tab>
            <Tab eventKey="configuration" title={t('renewals.tabs.configuration.title')}>
              <RenewalsConfigurationTab
                companyChecks={companyChecks}
                renewalConfigs={renewalConfigs}
                updateLocalRenewalConfigs={setRenewalConfigs}
                companyUsers={companyUsers}
                brands={brands}
                setLoading={setLoading}
              />
            </Tab>
          </OverflowTabs>
        )}
      </div>
    </AppPage>
  );
};

export default Renewals;
